import { ChangeEvent, FocusEvent, useState } from 'react';
import { CellProps } from 'react-table';
import TextField from '../../../../component/TextField';
import EditableCell from './EditableCell';
import { ServiceSplitHelper } from '../../../utils/ServiceSplitHelper';

const QuantityCell = ({ row, tableOptions }: CellProps<any>) => {
    const { updateRowData, adjustSplitQuantity, disableQuantityEdit } = tableOptions;
    const rowData = row.original;

    const currentQuantity = rowData?.attributes?.svcQuantity || 1;
    const [inputValue, setInputValue] = useState(currentQuantity);

    // Check if this is a merged row
    const isMergedRow = rowData?.isMergedRow || rowData?.isMerged;

    // If disabled in modal, show readonly
    if (disableQuantityEdit) {
        return (
            <div className="text-center line-height-30">
                {currentQuantity}
            </div>
        );
    }

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        if (value === '' || /^\d+$/.test(value)) {
            setInputValue(value === '' ? '' : parseInt(value));
        }
    };

    const handleBlur = (e: FocusEvent<HTMLInputElement>, setEnableEditing: any) => {
        const finalValue = inputValue === '' ? 1 : Math.max(1, parseInt(String(inputValue)));
        setInputValue(finalValue);

        // Check if row has edited split items from popup
        if (rowData._hasSplitItems && rowData._splitItems && finalValue !== currentQuantity) {
            // ⚠️ Warning: Row đã có split items từ popup
            const confirmed = window.confirm(
                `Dịch vụ này đã được chỉnh sửa chi tiết (${rowData._splitItems.length} items).\n\n` +
                `Thay đổi số lượng từ ${currentQuantity} → ${finalValue} sẽ XÓA tất cả chỉnh sửa chi tiết.\n\n` +
                `Bạn có chắc chắn muốn thay đổi?`
            );

            if (!confirmed) {
                // User cancel → Reset input, không thay đổi gì
                setInputValue(currentQuantity);
                setEnableEditing(false);
                return;
            }

            // User confirm → Clear split items, update quantity
            const updatedAttributes = {
                ...rowData.attributes,
                svcQuantity: finalValue
            };

            // Clear split items metadata
            const updatedRow = {
                ...rowData,
                attributes: updatedAttributes,
                _splitItems: undefined,
                _hasSplitItems: false,
            };

            // Update entire row to clear metadata
            updateRowData(row.index, null, updatedRow);
        }
        // Merged row → Adjust split items quantity
        else if (isMergedRow && adjustSplitQuantity) {
            adjustSplitQuantity(rowData.splitGroupId, finalValue);
        }
        // Normal row → Just update quantity
        else {
            const updatedAttributes = {
                ...rowData.attributes,
                svcQuantity: finalValue
            };
            updateRowData(row.index, "attributes", updatedAttributes);
        }

        setEnableEditing(false);
    };

    // Both merged and normal rows are editable
    return (
        <EditableCell
            className="spaces min-w-80px h-30px text-center"
            textValue={currentQuantity}
            textClassName="spaces line-height-30 text-center"
            editComponent={({ setEnableEditing }: any) => {
                return (
                    <TextField
                        className="w-100"
                        inputClassName="w-100 text-center"
                        name="svcQuantity"
                        type="number"
                        value={inputValue}
                        onChange={handleChange}
                        onBlur={(e: FocusEvent<HTMLInputElement>) => handleBlur(e, setEnableEditing)}
                        onKeyDown={(e: any) => {
                            if (e.key === 'Enter') {
                                handleBlur(e, setEnableEditing);
                            }
                        }}
                        autoFocus={true}
                        min={1}
                    />
                );
            }}
        />
    );
};

export default QuantityCell;
